import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Medication,
  MedicalServices,
  Share,
  Settings,
  ChevronLeft,
  Person,
  Logout,
  Notifications
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { APP_URLS } from '../../../../urls';
import LOGO from '../../../../../static/images/user/mtreat-transparentbg.png';
import { useDispatch, useSelector } from 'react-redux';
import { logoutPatient } from '../../../redux/middleware/user/patient/auth/login';
import { showToast } from '../../../utils/user/patient/helpers';
import { handleLogout } from '../../../utils/user/patient/authHelpers';
import { toast } from 'react-toastify';

const Sidebar = ({
  patientData,
  isSidebarExpanded,
  isSidebarVisible,
  isMobile,
  toggleSidebarExpansion,
  setIsSidebarVisible
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const sidebarRef = useRef(null);
  const [activeItem, setActiveItem] = useState(null);

  // Get sidebar state from Redux
  const { loading, error } = useSelector((state) => state.patientAuth);

  // Handle clicks outside the sidebar to close it on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobile &&
				isSidebarVisible &&
				sidebarRef.current &&
				!sidebarRef.current.contains(event.target)
      ) {
        setIsSidebarVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isSidebarVisible, setIsSidebarVisible]);

  // Set active item based on current path
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('dashboard')) setActiveItem('dashboard');
    else if (path.includes('purchases')) setActiveItem('purchases');
    else if (path.includes('treatments')) setActiveItem('treatments');
    else if (path.includes('share')) setActiveItem('share');
    else if (path.includes('settings')) setActiveItem('settings');
    else if (path.includes('notifications')) setActiveItem('notifications');
  }, [location]);

  // Navigation handler
  const handleNavigation = (path) => {
    navigate(path);
    setActiveItem(path);
    if (isMobile) {
      setIsSidebarVisible(false);
    }
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Home />,
      path: APP_URLS.patientDashboard
    },
    {
      id: 'purchases',
      label: 'Medical Purchases',
      icon: <Medication />,
      path: APP_URLS.patientPurchases
    },
    {
      id: 'treatments',
      label: 'Treatment History',
      icon: <MedicalServices />,
      path: APP_URLS.patientTreatments
    },
    {
      id: 'share',
      label: 'Share Medical Data',
      icon: <Share />,
      path: APP_URLS.patientShare
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: <Notifications />,
      path: APP_URLS.patientNotifications
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings />,
      path: APP_URLS.patientSettings
    }
  ];

  // Define sidebar animation variants based on mobile state
  const sidebarVariants = {
    expanded: {
      width: '250px',
      x: 0,
      transition: { type: 'spring', stiffness: 300, damping: 30 }
    },
    collapsed: {
      width: '75px',
      x: 0,
      transition: { type: 'spring', stiffness: 300, damping: 30 }
    },
    visible: {
      width: '250px',
      x: 0,
      transition: { type: 'spring', stiffness: 300, damping: 30 }
    },
    hidden: {
      width: '250px',
      x: '-100%',
      transition: { type: 'spring', stiffness: 300, damping: 30 }
    }
  };

  // Determine which animation state to use
  const currentVariant = isMobile
    ? isSidebarVisible
      ? 'visible'
      : 'hidden'
    : isSidebarExpanded
      ? 'expanded'
      : 'collapsed';

  // Should text be visible?
  const isTextVisible =
		currentVariant === 'expanded' || currentVariant === 'visible';

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && isSidebarVisible && (
        <div
          className={css(styles.overlay)}
          onClick={() => setIsSidebarVisible(false)}
        />
      )}

      <motion.div
        ref={sidebarRef}
        className={css(styles.sidebar)}
        variants={sidebarVariants}
        animate={currentVariant}
        initial={isMobile ? 'hidden' : 'expanded'}
      >
        <div className={css(styles.sidebarHeader)}>
          <div
            className={css(styles.logoContainer)}
            onClick={() => navigate(APP_URLS.home)}
          >
            <img
              src={LOGO || '/placeholder.svg'}
              alt='Company Logo'
              className={css(styles.logo)}
            />
            <AnimatePresence>
              {isTextVisible && (
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={css(styles.logoText)}
                >
                  M-TREAT
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          {/* Only show toggle button on desktop */}
          {!isMobile && (
            <motion.button
              className={css(styles.toggleButton)}
              onClick={toggleSidebarExpansion}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeft
                style={{
								  transform: isSidebarExpanded
								    ? 'rotate(0deg)'
								    : 'rotate(180deg)'
                }}
              />
            </motion.button>
          )}
        </div>

        {/* Profile section */}
        <div className={css(styles.profileSection)}>
          <div className={css(styles.avatar)}>
            <Person style={{ fontSize: 32 }} />
          </div>
          <AnimatePresence>
            {isTextVisible && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className={css(styles.profileInfo)}
              >
                <h3 className={css(styles.profileName)}>
                  {patientData?.first_name} {patientData?.last_name}
                </h3>
                <p className={css(styles.profileId)}>
                  ID: {patientData?.user_id || 'N/A'}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className={css(styles.menuItems)}>
          {menuItems.map((item) => (
            <motion.div
              key={item.id}
              className={css(
							  styles.menuItem,
							  activeItem === item.id && styles.activeItem
              )}
              onClick={() => handleNavigation(item.path)}
              whileHover={{ backgroundColor: '#e8f5f3' }}
              whileTap={{ scale: 0.98 }}
            >
              <div className={css(styles.menuIcon)}>{item.icon}</div>
              <AnimatePresence>
                {isTextVisible && (
                  <motion.span
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className={css(styles.menuLabel)}
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <div className={css(styles.sidebarFooter)}>
          <motion.div
            className={css(styles.logoutButton)}
            onClick={() => handleLogout(toast, dispatch, navigate)}
            whileHover={{ backgroundColor: '#ffebee' }}
            whileTap={{ scale: 0.98 }}
          >
            <div className={css(styles.menuIcon)}>
              {loading
                ? (
                  <CircularProgress size={24} color='inherit' />
                  )
                : (
                  <Logout />
                  )}
            </div>
            <AnimatePresence>
              {isTextVisible && (
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={css(styles.menuLabel)}
                >
                  {loading ? 'Logging out...' : 'Logout'}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 998
  },
  sidebar: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    backgroundColor: 'white',
    boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 999,
    overflow: 'hidden'
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    overflow: 'hidden',
    cursor: 'pointer'
  },
  logo: {
    width: '32px',
    height: '32px',
    marginRight: '0.5rem',
    objectFit: 'contain'
  },
  logoText: {
    fontWeight: 'bold',
    fontSize: '1.2rem',
    color: '#2783F7',
    whiteSpace: 'nowrap'
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    borderBottom: '1px solid #e0e0e0'
  },
  toggleButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#2783F7'
  },
  profileSection: {
    display: 'flex',
    alignItems: 'center',
    padding: '1rem',
    borderBottom: '1px solid #e0e0e0'
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#e6f0fd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#2783F7',
    marginRight: '1rem',
    flexShrink: 0
  },
  profileInfo: {
    overflow: 'hidden'
  },
  profileName: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#333'
  },
  profileId: {
    margin: 0,
    fontSize: '0.8rem',
    color: '#666'
  },
  menuItems: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem 0',
    overflowY: 'auto'
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    borderRadius: '0 20px 20px 0',
    marginBottom: '0.5rem'
  },
  activeItem: {
    backgroundColor: '#e6f0fd',
    color: '#2783F7',
    fontWeight: 'bold',
    borderLeft: '4px solid #2783F7'
  },
  menuIcon: {
    marginRight: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#2783F7',
    minWidth: '24px'
  },
  menuLabel: {
    whiteSpace: 'nowrap',
    color: '#000000'
  },
  sidebarFooter: {
    padding: '1rem',
    borderTop: '1px solid #e0e0e0'
  },
  logoutButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    borderRadius: '20px',
    color: '#f44336'
  }
});

export default Sidebar;
